import { useState } from 'react';
import { FormikHelpers, useFormik } from 'formik';
import { Typography, TextField, Button, Alert, InputAdornment, FormHelperText } from '@mui/material';

import useOrganizations from 'src/hooks/superAdmin/useOrganizations';
import useModals from 'src/hooks/useModals';
import useSnackbar from 'src/hooks/useSnackbar';
import { attributesToSubmit, initialValues, ChangeRateFormData, validationSchema } from 'src/forms/changeRate';
import { Organization } from 'src/types/resources/Organization';
import {
  extractResponseErrors,
  isAxiosError,
  isUnprocessedEntityError,
  parseToFormikErrors,
} from 'src/utils/responseErrors';
import Box from 'src/components/Box';

import styles from './styles';

type ChangeRateFormProps = {
  organization: Organization;
};

const ChangeRateForm: React.FC<ChangeRateFormProps> = props => {
  const { organization } = props;
  const [formError, setFormError] = useState<string | null>(null);
  const { showOrganization, updateOrganization } = useOrganizations();
  const { hideModal } = useModals();
  const { enqueueSuccessSnackbar } = useSnackbar();

  const handleSubmit = async (formData: ChangeRateFormData, { setErrors }: FormikHelpers<ChangeRateFormData>) => {
    const params = attributesToSubmit(formData, organization.name);
    setFormError(null);
    try {
      await updateOrganization({
        id: organization.id,
        params: { ...params, accountType: organization.accountType },
      }).unwrap();
      enqueueSuccessSnackbar('Rate was changed');
      showOrganization({ id: organization.id });
      hideModal();
    } catch (error: unknown) {
      if (isAxiosError(error) && isUnprocessedEntityError(error)) {
        const errors = extractResponseErrors(error);
        setFormError(error.response.data.errors.nonFieldErrors);
        setErrors(parseToFormikErrors(errors));
      }
    }
  };

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit: handleFormikSubmit,
  } = useFormik<ChangeRateFormData>({
    initialValues: initialValues(organization),
    validationSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
  });

  return (
    <>
      {formError && <Alert severity="error">{formError}</Alert>}
      <form noValidate onSubmit={handleFormikSubmit}>
        <Box sx={styles.wrapper}>
          <Typography variant="h3" sx={styles.title}>
            Change Rate
          </Typography>

          <Box sx={styles.row}>
            <Typography variant="button">
              Rate appears in the Talent Engagements and Organization Timekeeping.
            </Typography>
            <Typography variant="body1">
              It defines the Per Hour Rate the Organization will be paying for the Talent. 10% is default. You can
              change the percentage below.
            </Typography>
          </Box>

          <Box sx={styles.row}>
            <Box sx={styles.rateWrapper}>
              <Typography variant="body1">Talent Hourly Rate +</Typography>
              <TextField
                id="rate"
                type="number"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.rate}
                error={touched.rate && Boolean(errors.rate)}
                InputProps={{
                  sx: styles.rateInput,
                  value: values.rate,
                  endAdornment: (
                    <InputAdornment sx={styles.rateInputAdornment} position="end">
                      %
                    </InputAdornment>
                  ),
                }}
                sx={styles.rateField}
              />
            </Box>
            {touched.rate && errors.rate && <FormHelperText error>{errors.rate}</FormHelperText>}
          </Box>

          <Box sx={styles.footer}>
            <Button type="submit" variant="contained" sx={styles.button} disabled={isSubmitting}>
              Change
            </Button>
            <Button variant="outlined" onClick={hideModal} sx={styles.button}>
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default ChangeRateForm;
